import React, { Fragment } from 'react'
import { PATHS } from '~shared/constants/paths'
import { useRouter } from 'next/router'
import Script from 'next/script'
import { NextSeo } from 'next-seo'
import { HeaderUpdated } from '~widgets/header/components/header-updated'
import { Footer } from '~widgets/footer/components/footer'
import { getFooterData } from '~shared/api/footer/getFooterData'
import { getPopularPrograms } from '~shared/api/programs-popular/getPopularPrograms'
import { getCategoriesData } from '~shared/api/categories/getCategories'
import { HeroSection } from '~pages/program-page/components/program-hero-section'
import { ForWhomSection } from '~pages/program-page/components/program-for-whom'
import { CommunitySupportSection } from '~pages/program-page/components/program-community-section'
import { LeastProgramContentSection } from '~pages/program-page/components/program-least-program-content'
import { WhatWillYouLearnSection } from '~pages/program-page/components/program-what-will-you-learn'
import { AboutTraining } from '~pages/program-page/components/program-about-training-section'
import { StudyIsSection } from '~pages/program-page/components/program-study-is-section'
import { DiplomasSection } from '~pages/program-page/components/program-diploma-section'
import { CorporateLeadSection } from '~pages/program-page/components/program-corporated-lead-section'
import { CostSingleSection } from '~pages/program-page/components/program-cost-single-section'
import { CostMixedSection } from '~pages/program-page/components/program-cost-mixed-section'
import { getProgramData } from '~shared/api/single-program-data/single-program-data'
import { FullProgramContents } from '~pages/program-page/components/program-full-program-content-section'
import { StudyStepsSection } from '~pages/program-page/components/program-study-steps-section'
import { ExamFreeEnrollmentSection } from '~pages/program-page/components/program-exam-free-enrollment'
import { LeastDocumentsSection } from '~pages/program-page/components/program-least-documents'
import { GraduateProfessionsSection } from '~pages/program-page/components/program-graduate-professions'
import { ReviewsSection } from '~pages/program-page/components/program-reviews-section'
import { FAQSection } from '~pages/program-page/components/program-faq-section'
import { ProgramsArchiveSection } from '~pages/program-page/components/program-archive-section'
import { HeroArchiveSection } from '~pages/program-page/components/program-hero-archive-section'
import { LeadFormPsychologySection } from '~pages/program-page/components/program-lead-form-psychology-section'
import { Breadcrumbs } from '~widgets/breadcrumbs/breadcrumbs'
import { TeachersSection } from '~pages/program-page/components/program-teachers-section'
import { CostSection } from '~pages/program-page/components/program-cost-section'
import { ProgramMarketingForPsySection } from '~pages/program-page/components/program-psy-for-marketing'
import { LicenseAndAccreditationSection } from '~pages/program-page/components/program-license-accreditation-section'
import { ProgramLicenseSection } from '~pages/program-page/components/program-license-section'

type ApiResult<T extends (...args: any) => any> = Awaited<ReturnType<T>>

export type ProgramPageProps = {
  program: ApiResult<typeof getProgramData>
  footer: ApiResult<typeof getFooterData>
  popularPrograms: ApiResult<typeof getPopularPrograms>
  categories: ApiResult<typeof getCategoriesData>
}

// ─── Slug helpers ─────────────────────────────────────────────────────────────
const PSYCHOLOGY_SLUGS = [
  'psiholog-konsultant',
  'klinicheskaya-psihologiya-2',
  'semejnaya-psihologiya',
  'seksologiya-v-psihologicheskom-konsultirovanii',
  'detskaya-psihologiya'
]
const hasCommunitySupport = (program: ApiResult<typeof getProgramData>) =>
  PSYCHOLOGY_SLUGS.includes(program?.slug ?? '')

const hasMixedCost = (program: ApiResult<typeof getProgramData>) =>
  (program?.costOffline?.priceOffline && program?.cost?.price) ||
  (program?.costOnline?.priceOnline && program?.cost?.price) ||
  (program?.costOnline?.priceOnline && program?.costOffline?.priceOffline)

export const ProgramPage = ({
  program,
  footer,
  categories,
  popularPrograms
}: ProgramPageProps) => {
  const { asPath } = useRouter()
  const categorySlug = program?.category?.slug ?? ''
  const facultySlug = program?.faculty?.slug ?? ''

  const isCourse = categorySlug === 'kursy'
  const isBachelor = categorySlug === 'bakalavriat'
  const isMaster = categorySlug === 'magistratura'
  const isHigherEducation = isBachelor || isMaster || categorySlug === 'mba'
  const isPsychology = facultySlug === 'psihologiya'
  const isInformatika = facultySlug === 'prikladnaya-informatika'
  const isMenedzhment = facultySlug === 'menedzhment'
  const isMba = categorySlug === 'mba'
  const isColledge = categorySlug === 'college'
  const sections = [
    // ─── Hero ───────────────────────────────────────────────────────────────
    {
      rank: 100,
      Section: program?.isArchive ? (
        <>
          <HeroArchiveSection program={program} />
          <ProgramsArchiveSection program={program} />
        </>
      ) : (
        <HeroSection program={program} />
      )
    },

    // ─── For whom ────────────────────────────────────────────────────────────
    {
      rank: 200,
      Section: <ForWhomSection program={program} />
    },

    // ─── Exam free enrollment (бакалавриат / группа выходного дня) ──────────
    {
      rank: isCourse ? 500 : 300,
      Section:
        !program?.isArchive && isBachelor ? (
          <ExamFreeEnrollmentSection program={program} />
        ) : null
    },

    // ─── Study steps ─────────────────────────────────────────────────────────
    {
      rank: isCourse ? 700 : 400,
      Section: <StudyStepsSection program={program} />
    },

    // ─── Community support (только для нужных психологических слагов) ───────
    {
      rank: 450,
      Section: hasCommunitySupport(program) ? <CommunitySupportSection /> : null
    },

    // ─── Full / Least program contents ───────────────────────────────────────
    {
      rank: isCourse ? 600 : 500,
      Section: program?.fullProgramContents ? (
        <>
          <FullProgramContents program={program} />{' '}
          {isPsychology ? (
            <LeadFormPsychologySection program={program} />
          ) : null}
        </>
      ) : (
        <LeastProgramContentSection
          program={program}
          isLeadForm={!program?.isArchive}
        />
      )
    },

    // ─── What will you learn ──────────────────────────────────────────────────
    {
      rank: isCourse ? 300 : 600,
      Section: <WhatWillYouLearnSection program={program} />
    },

    //─── Marketing for psy─────────────────────────────────────────────────
    {
      rank: 350,
      Section:
        !program?.isArchive &&
        isPsychology &&
        isCourse &&
        program?.slug !== 'zdorove-bystro-prosto-effektivno' ? (
          <ProgramMarketingForPsySection
            institute={program?.institute?.name}
            uid={program?.productUid}
            name={program?.name}
          />
        ) : null
    },

    // ─── About training (только курсы) ───────────────────────────────────────
    {
      rank: 400,
      Section: isCourse ? <AboutTraining /> : null
    },

    // ─── Study is ────────────────────────────────────────────────────────────
    {
      rank: isPsychology ? (isCourse ? 690 : 590) : isCourse ? 800 : 700,
      Section: <StudyIsSection program={program} />
    },

    // ─── Teachers (только курсы) ──────────────────────────────────────────────
    {
      rank: 1000,
      Section: isCourse ? <TeachersSection program={program} /> : null
    },

    // ─── License And Accreditation ────────────────────────────────────────────────────────────
    {
      rank: isCourse ? 1100 : 900,
      Section:
        isMba ||
        isColledge ||
        ['Синергия', 'МосАП', 'МТИ']?.includes(
          program?.institute?.name ?? ''
        ) ? null : (isPsychology || isInformatika || isMenedzhment) &&
          isBachelor ? (
          <LicenseAndAccreditationSection program={program} />
        ) : (
          <ProgramLicenseSection program={program} />
        )
    },
    // ─── Diplomas ────────────────────────────────────────────────────────────
    {
      rank: isCourse ? 1101 : 901,
      Section: !isMba && <DiplomasSection program={program} />
    },

    // ─── Least documents ─────────────────────────────────────────────────────
    {
      rank: 1010,
      Section: isHigherEducation ? (
        <LeastDocumentsSection program={program} />
      ) : null
    },

    // ─── Graduate professions ────────────────────────────────────────────────
    {
      rank: isCourse ? 1300 : 1200,
      Section: <GraduateProfessionsSection program={program} />
    },

    // ─── Cost ────────────────────────────────────────────────────────────────
    {
      rank: isCourse ? 1500 : 1300,
      Section: !program?.isArchive ? (
        hasMixedCost(program) ? (
          <CostMixedSection program={program} />
        ) : isBachelor || isMaster || isColledge ? (
          <CostSingleSection program={program} />
        ) : (
          <CostSection program={program} />
        )
      ) : null
    },

    // ─── Reviews ─────────────────────────────────────────────────────────────
    {
      rank: 1400,
      Section: <ReviewsSection program={program} />
    },

    // ─── Corporate lead (только курсы) ───────────────────────────────────────
    {
      rank: 1700,
      Section:
        isCourse && !program?.isArchive ? (
          <CorporateLeadSection program={program} />
        ) : null
    },

    // ─── FAQ ─────────────────────────────────────────────────────────────────
    {
      rank: isCourse ? 1800 : 1400,
      Section: <FAQSection program={program} isLeadForm={!program?.isArchive} />
    }
  ]
    .filter(({ Section }) => Boolean(Section))
    .sort((a, b) => a.rank - b.rank)

  const seoParams = {
    title:
      program?.seo?.metaTitle ||
      (isBachelor &&
        `${program?.name || 'Программа'}: обучение онлайн | ${
          program?.category?.name || 'Обучение'
        } - дистанционное высшее образование`) ||
      `${program?.name || 'Программа'}: обучение дистанционно | ${
        program?.category?.name || 'Обучение'
      } от онлайн-института MITM` ||
      program?.name ||
      null,

    desc:
      program?.seo?.metaDescription ||
      (isCourse &&
        `Обучение по курсу "${
          program?.name || 'Программа'
        }" от самого инновационного ВУЗа страны - MITM.institute. Онлайн - курс дополнительного профессионального образования (ДПО) вместе с ведущими экспертами-практиками. Дистанционный формат обучения ➜ Скидка - 30%`) ||
      (isMaster &&
        `Обучение по курсу "${
          program?.name || 'Программа'
        }" от самого инновационного ВУЗа страны - MITM.institute. Вторая ступень высшего образования магистратура образование вместе с экспертами-практиками ➜ Дистанционный формат обучения. Диплом государственного образца! Скидка - 30%`) ||
      (isBachelor &&
        `Обучение по курсу "${
          program?.name || 'Программа'
        }" от самого инновационного ВУЗа страны - MITM.institute. Высшее образование (бакалавриат) вместе с экспертами-практиками ➜ Дистанционный формат обучения. Диплом государственного образца! Скидка - 30%`) ||
      program?.description ||
      null,

    canonical:
      program?.seo?.canonicalURL || `${PATHS.front.root.index}${asPath}`
  }

  return (
    <>
      <Script
        id='Product'
        type='application/ld+json'
        strategy='afterInteractive'
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: program?.name,
            offers: {
              '@type': 'AggregateOffer',
              priceCurrency: 'RUB',
              lowPrice: String(
                Math.ceil(
                  (program?.cost?.price ||
                    program?.costOffline?.priceOffline ||
                    program?.costOnline?.priceOnline) ??
                    60000 / 12
                )
              ),
              availability: 'https://schema.org/InStock'
            },
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: 4.78,
              ratingCount: 182,
              bestRating: 5,
              worstRating: 1
            }
          })
        }}
      />
      <NextSeo
        title={seoParams.title || undefined}
        description={seoParams.desc || undefined}
        canonical={seoParams.canonical}
        nofollow={!program?.seo?.isSEOFriendly}
        noindex={!program?.seo?.isSEOFriendly}
        openGraph={{
          url: seoParams.canonical,
          title: seoParams.title || undefined,
          description: seoParams.desc || undefined,
          images: [
            {
              url: `${PATHS.front.root.index}${PATHS.front.public.assets.imgs.icons.mitmLogo512.index}`,
              width: 512,
              height: 512,
              alt: 'Московский Институт Технологий и Управления',
              type: 'image/png'
            }
          ],
          site_name: 'Московский Институт Технологий и Управления'
        }}
      />
      <HeaderUpdated
        categories={categories}
        popularPrograms={popularPrograms}
      />
      <main
        itemType='https://schema.org/Product'
        itemScope
        style={{ overflowX: 'hidden' }}>
        <Breadcrumbs program={program} />
        {sections.map(({ Section, rank }, idx) => (
          <Fragment key={rank + idx}>{Section}</Fragment>
        ))}
      </main>
      <Footer data={footer} />
    </>
  )
}
